/**
 * The project's overview tab: how much of the budget is gone, how far the work
 * has got, what the money went on, and the terms the project was set up on.
 *
 * The five headline figures live in the page frame above this, not here - they
 * are true on every tab, and a strip that appears and disappears as you move
 * between tabs reads as the page reloading.
 *
 * Everything the cost card shows has been computed by the summary endpoint
 * since the day it was written and thrown away here. That is the reason for the
 * one label this file argues with itself about: `laborCostCents` is billable
 * minutes times the SELLING rate, so it is called billable work, not labour
 * cost. We do not store a cost rate, and a confident wrong number is worse than
 * the honest hole it replaced.
 *
 * Three cards are not drawn here. What was delivered and where it is
 * administered are the only facts on this tab that come from the record rather
 * than from the summary endpoint, they have their own emptiness rules and their
 * own link parsing, and they live in `DeliveryCard.tsx`. The progress card left
 * for a different reason: it stopped being markup. It now holds a local slider
 * value, a rule deciding which of two percentages is the true one and a preview
 * of what changing that rule would cost, and a state machine sitting among
 * label-and-figure rows is a state machine nobody reviews (`ProgressCard.tsx`).
 *
 * What stayed is the layout and the cards that are only rows: the money, the
 * task counts, the terms - and the team strip, which is three lines of markup
 * over the shared person primitives and has no rule beyond "empty or not".
 */
import type { ReactNode } from "react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { Project, ProjectSummary } from "../../../lib/projects";
import { Card, CardHead, fmtDate, fmtDateTime, fmtNum, fmtPct } from "../../ui";
import { UserAvatars, UserName } from "../_shared/UserChips";
import { DeliveryCard, ProjectLinksCard } from "./DeliveryCard";
import { ProgressCard } from "./ProgressCard";
import { fmtHours, money } from "./lib";
import { TASK_STATUSES } from "./status";

/** One label-and-figure line in a facts card. Hairlines between, none on top. */
function Row({ label, value, strong }: { label: string; value: ReactNode; strong?: boolean }) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 border-t border-[var(--crm-hair)] py-2.5 text-[13px] first:border-t-0 first:pt-0",
        strong && "font-bold",
      )}
    >
      <span className={strong ? undefined : "crm-mut"}>{label}</span>
      <span className="crm-push crm-tnum font-semibold">{value}</span>
    </div>
  );
}

/**
 * Who is on this project.
 *
 * A strip rather than a column: a project team is three or four people, and a
 * card with a heading and a vertical list would give a one-line fact the height
 * of the cost breakdown next to it.
 *
 * Always drawn, including - especially - when it is empty, which today it is on
 * every imported project: the Perfex import brought no owners across, because
 * only one platform user exists to hand them to. So the sentence is the state
 * most people will actually meet, and it has to read as an answer ("nobody has
 * been named") rather than as a card that failed to load. That is also why the
 * label sits beside it: "Team - nobody assigned yet" is a finished thought,
 * while a bare grey sentence in an unlabelled box is a spinner that gave up.
 *
 * Adding and removing people is not here. Membership is a field of the project
 * record and is edited in the project form, next to the rest of it; a second
 * writing surface would be a second place to go wrong about the same column.
 */
function TeamCard({ members }: { members: string[] }) {
  const t = useT();
  // The same id twice comes from a members list edited twice. `UserAvatars`
  // dedupes for its own React keys; the names beside it have to agree with the
  // faces, or the strip draws four discs and lists five people.
  const ids = [...new Set(members)];

  return (
    <Card className="flex flex-wrap items-center gap-x-4 gap-y-2.5 p-5">
      <span className="crm-mut shrink-0 text-[12.75px]">{t("projects.team")}</span>
      {ids.length ? (
        <>
          <UserAvatars ids={ids} max={8} />
          <span className="min-w-0 text-[13px]">
            {ids.map((id, i) => (
              <span key={id}>
                {i > 0 ? ", " : null}
                <UserName id={id} />
              </span>
            ))}
          </span>
        </>
      ) : (
        <span className="crm-mut text-[13px] leading-relaxed">{t("projects.noMembers")}</span>
      )}
    </Card>
  );
}

/**
 * Planned hours beside the hours actually logged.
 *
 * The rule this exists for: `estimatedHours` is null on every project in the
 * register today - the column was added in this ring and the Perfex import never
 * had one to bring across - so what this draws for a null IS what the whole
 * register looks like. It prints an explicit "-" for the planned half rather
 * than a zero, because a project nobody estimated is not a project estimated at
 * zero hours: one of those is over budget on the first logged minute and the
 * other is simply unknown, and a screen that cannot tell them apart teaches
 * everyone to ignore the row.
 *
 * The row is drawn even so. Hiding it until somebody fills the field in would
 * make the field undiscoverable in exactly the state everybody meets it in, the
 * way the empty team strip would have been - a dash is an answer, an absent row
 * is not.
 *
 * The over/under only appears when there IS an estimate, for the same reason.
 * Against a null there is no overrun to report, only a subtraction nobody asked
 * for.
 */
function PlannedVsActual({ estimatedHours, totalMinutes }: { estimatedHours: number | null; totalMinutes: number }) {
  // Through `fmtHours` rather than formatted here, so both halves of one row are
  // written the same way. It takes minutes, hence the multiplication.
  const actual = fmtHours(totalMinutes);
  if (estimatedHours == null) return <span>{`- / ${actual} h`}</span>;

  const overBy = totalMinutes / 60 - estimatedHours;
  return (
    <span>
      {`${fmtHours(estimatedHours * 60)} / ${actual} h`}
      {/* A hundredth of an hour of slack, which is the finest thing `fmtHours`
          can print: below that the badge would say "+0 h", and a difference of
          zero rendered as a difference is worse than no badge at all. */}
      {Math.abs(overBy) >= 0.01 ? (
        <span className={cn("ml-1.5 font-semibold", overBy > 0 ? "text-red-600" : "crm-mut")}>
          {`${overBy > 0 ? "+" : "-"}${fmtHours(Math.abs(overBy) * 60)} h`}
        </span>
      ) : null}
    </span>
  );
}

export function ProjectOverview({
  project,
  summary,
  onProgress,
  onProgressSource,
}: {
  project: Project;
  summary: ProjectSummary | null;
  onProgress: (v: number) => void;
  onProgressSource: (s: Project["progressSource"]) => void;
}) {
  const t = useT();
  const used = summary?.budgetUsedPct ?? null;
  // The verdict comes from the API, the percentage only draws the bar. Deriving
  // it here from `used > 100` used the rounded percent and disagreed with the
  // register header for every project overspent by less than half a percent.
  const overBudget = summary?.overBudget ?? false;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        {used != null ? (
          <Card className="p-5">
            <div className="flex items-baseline gap-3 text-[12.75px]">
              <span className="crm-mut">{t("projects.budgetUsed")}</span>
              <span className={cn("crm-push crm-tnum text-[15px] font-bold", overBudget && "text-red-600")}>
                {fmtPct(used, 0)}
              </span>
            </div>
            <span className="crm-fbar">
              <i
                style={{
                  width: `${Math.min(used, 100)}%`,
                  background: overBudget
                    ? "var(--color-destructive)"
                    : "linear-gradient(90deg, var(--color-primary), var(--color-primary-2))",
                }}
              />
            </span>
          </Card>
        ) : null}

        <ProgressCard
          project={project}
          summary={summary}
          onProgress={onProgress}
          onProgressSource={onProgressSource}
        />
      </div>

      <TeamCard members={project.members} />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHead title={t("projects.details")} />
          <div className="px-6 pb-5 pt-3">
            {/* Prisma defaults the column to "fixed", so a null here is not a
                fixed-price project: it is an imported row where nobody said.
                Printing "Fixed" for it would invent the one term the whole
                invoice hangs off. */}
            <Row
              label={t("projects.billingType")}
              value={project.billingType ? t(`projects.billing.${project.billingType}`) : "-"}
            />
            {project.hourlyRateCents != null ? (
              <Row label={t("projects.hourlyRate")} value={`${money(project.hourlyRateCents, true)}/h`} />
            ) : null}
            <Row label={t("projects.budget")} value={money(project.budgetCents)} />
            <Row label={t("projects.startDate")} value={fmtDate(project.startDate)} />
            <Row label={t("projects.deadline")} value={fmtDate(project.deadline)} />
            <Row label={t("projects.completedAt")} value={fmtDate(project.completedAt)} />
            {/* The newest of three things: the project row's own `updatedAt`,
                its newest task and its newest time entry. NOT "somebody opened
                the page" and not the header's edit stamp - a project whose
                tasks moved all week has not been idle because nobody retyped
                its budget. It is the only row in this card that comes from the
                summary rather than from the record, which is why it prints "-"
                on its own when the summary fetch is the thing that failed.

                A stamp rather than a date, unlike its four neighbours: the
                question this answers is "is anyone still working on this", and
                a bare day cannot separate this morning from last night. */}
            <Row label={t("projects.lastActivity")} value={fmtDateTime(summary?.lastActivityAt)} />
          </div>
        </Card>

        <Card>
          <CardHead title={t("projects.costBreakdown")} />
          <div className="px-6 pb-5 pt-3">
            {summary ? (
              <>
                <Row label={t("projects.labourBillable")} value={money(summary.laborCostCents)} />
                <Row label={t("projects.expenses")} value={money(summary.expenseCents)} />
                <Row label={t("projects.spent")} value={money(summary.spentCents)} strong />
                {/* The hours block opens with what was promised, because the
                    three rows under it all divide up the same logged time and
                    none of them can say whether there was too much of it. */}
                <Row
                  label={t("projects.plannedVsActual")}
                  value={
                    <PlannedVsActual estimatedHours={project.estimatedHours} totalMinutes={summary.totalMinutes} />
                  }
                />
                {/* Billable of total, then how much of the billable has been
                    asked for. Non-billable time is in neither of the last two
                    rows on purpose: it will never reach an invoice, and calling
                    it unbilled would promise revenue that does not exist. */}
                <Row
                  label={t("projects.billableHours")}
                  value={`${fmtHours(summary.billableMinutes)} / ${fmtHours(summary.totalMinutes)} h`}
                />
                <Row label={t("projects.billedHours")} value={`${fmtHours(summary.billedMinutes)} h`} />
                <Row label={t("projects.unbilledHours")} value={`${fmtHours(summary.unbilledMinutes)} h`} />
              </>
            ) : (
              <p className="crm-mut text-[13px]">-</p>
            )}
          </div>
        </Card>

        <Card>
          <CardHead title={t("projects.tasks")} />
          <div className="px-6 pb-5 pt-3">
            {summary ? (
              <>
                {TASK_STATUSES.map((s) => (
                  <Row key={s} label={t(`tasks.status.${s}`)} value={fmtNum(summary.taskCounts[s])} />
                ))}
                {/* Overdue is not repeated here: it is one of the five figures
                    in the strip above, which is on screen on every tab. */}
                <Row label={t("projects.totalTasks")} value={fmtNum(summary.taskCounts.total)} strong />
              </>
            ) : (
              <p className="crm-mut text-[13px]">-</p>
            )}
          </div>
        </Card>
      </div>

      {/* Always drawn, on every project, including the ones where both are
          empty. These two answer "what did we build" and "where do I log in",
          and a card that appears only once somebody has already filled it in is
          a card nobody ever finds in order to fill it in. Each says so itself
          when it has nothing. */}
      <div className="grid gap-4 lg:grid-cols-2">
        <DeliveryCard project={project} />
        <ProjectLinksCard project={project} />
      </div>

      {/* The brief, as it was written. `whitespace-pre-wrap` is the whole card:
          the text is stored with real newlines - the Perfex importer turns every
          `<br>` into "\n", and the project form is a textarea - and without it the
          browser folds every one of them into a space, so a phase-by-phase brief
          is drawn as a single paragraph. Same class as the task body card. */}
      {project.description ? (
        <Card className="whitespace-pre-wrap px-6 py-5 text-[13.25px] leading-relaxed">{project.description}</Card>
      ) : null}
    </div>
  );
}
